import { useCallback } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

const storageKey = (userId: number) =>
  `tt.seenAccountCompletions.v1.${userId}`

export const SEEN_ACCOUNT_COMPLETIONS_QUERY_KEY = [
  "requests",
  "seen-account-completions",
] as const

/** Idle poll for buyer Accounts badge (completed but unseen). */
export const BUYER_ACCOUNT_COMPLETIONS_IDLE_POLL_MS = 5 * 60_000

function readSeenIds(userId: number): number[] {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0)
  } catch {
    return []
  }
}

function writeSeenIds(userId: number, ids: number[]): number[] {
  const unique = [...new Set(ids)].sort((a, b) => a - b)
  if (typeof window !== "undefined") {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(unique))
  }
  return unique
}

export function useSeenAccountCompletions(userId: number | null | undefined) {
  return useQuery({
    queryKey: [...SEEN_ACCOUNT_COMPLETIONS_QUERY_KEY, userId ?? 0],
    queryFn: () => readSeenIds(userId as number),
    enabled: typeof userId === "number" && userId > 0,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

/** Mark completed account-request IDs as seen (clears Accounts sidebar badge). */
export function useMarkAccountCompletionsSeen(
  userId: number | null | undefined,
) {
  const qc = useQueryClient()
  return useCallback(
    (ids: number[]) => {
      if (typeof userId !== "number" || userId <= 0 || ids.length === 0) return
      const existing = readSeenIds(userId)
      const next = writeSeenIds(userId, [...existing, ...ids])
      qc.setQueryData([...SEEN_ACCOUNT_COMPLETIONS_QUERY_KEY, userId], next)
    },
    [qc, userId],
  )
}

export function countUnseenCompletedAccountRequests(
  rows: Array<{ id: number; status: string }>,
  seenIds: number[] | undefined,
): number {
  const seen = new Set(seenIds ?? [])
  return rows.filter((r) => r.status === "completed" && !seen.has(r.id)).length
}
